/**
 * The robot opponent's speech bubble, P5-adjacent plumbing (P3's rendering
 * half): fires the persona's game-start line and the win/lose/encourage line
 * on the gameover transition, and renders `<PersonaBubble/>`.
 *
 * Extracted from GameScreen because the old inline copy sat AFTER the
 * component's `return` — unreachable dead code, so the bubble never rendered
 * at all. GameScreen itself mounts a react-three-fiber `<Canvas>` and cannot
 * be unit-rendered; this component can, and is.
 *
 * Suppression (checked BEFORE firing so the per-game spoken counter is not
 * consumed either):
 * - `AUDIT_CAPTURE=1` — screen audits keep pixels deterministic (spec risk
 *   row: "audit mode keeps bubbles, music and timers off"), and every audit
 *   workflow boots the dev server with the flag;
 * - `?engine=fake` — the scripted-engine e2e surface asserts DOM the same way
 *   goldens do; a bubble appearing there would churn specs for no signal.
 */
import type { PersonaEvent } from "@chessmate/persona";
import { useEffect, useRef, useState } from "react";
import { useT } from "../i18n/useT.js";
import { parseQuery } from "../lib/query.js";
import { useGameStore } from "../state/game-store.js";
import { usePersonaStore } from "../state/persona-store.js";
import { PersonaBubble } from "./PersonaBubble.js";

function suppressed(): boolean {
  if (import.meta.env.AUDIT_CAPTURE === "1") return true;
  return parseQuery().engine === "fake";
}

/** The persona's current line (null while quiet). Shared by the bubble panel and the opponent's seat plate. */
export function useRobotLine(): string | null {
  const { i18n } = useT("games");
  const locale = (i18n.language || "en").slice(0, 2) as "en" | "fr" | "es";
  const [line, setLine] = useState<string | null>(null);

  // Game start.
  useEffect(() => {
    if (suppressed()) return;
    usePersonaStore.getState().resetSpoken();
    setLine(usePersonaStore.getState().lineFor("game-start", locale));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Game over: map the result to the persona's event vocabulary.
  const phase = useGameStore((s) => s.phase);
  const prevPhase = useRef<string | null>(phase);
  useEffect(() => {
    if (phase === "gameover" && prevPhase.current !== "gameover") {
      if (!suppressed()) {
        const result = useGameStore.getState().pos.result;
        const event: PersonaEvent = result
          ? "win" in result || "winner" in result
            ? (result as { winner?: string }).winner === "w"
              ? "win"
              : "lose"
            : "encourage"
          : "encourage";
        setLine(usePersonaStore.getState().lineFor(event, locale));
      }
    }
    prevPhase.current = phase;
  }, [phase, locale]);

  return line;
}

export function RobotBubble() {
  const personaId = usePersonaStore((s) => s.personaId);
  const line = useRobotLine();
  return <PersonaBubble personaId={personaId} line={line} />;
}
